"use client";

import { useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Link, useRouter } from "@/i18n/navigation";
import { Button, useToast } from "@/components/ui";
import { Wordmark } from "@/components/shell";
import { TurnstileWidget } from "@/components/TurnstileWidget";
import { register, uploadMedia, newUploadIdempotencyKey, ApiError } from "@/lib/api";
import { getMe, saveMe } from "@/lib/auth-store";
import {
  addDraftPhoto,
  clearDraftPhotos,
  clearJoinDraft,
  listDraftPhotos,
  loadJoinDraft,
  removeDraftPhoto,
  saveJoinDraft,
} from "@/lib/join-draft";
import { useTurnstile } from "@/lib/use-turnstile";
import type { ProfileType } from "@/lib/types";
import { Stepper } from "./Stepper";
import { AccountTypeStep } from "./steps/AccountTypeStep";
import { PartnersStep } from "./steps/PartnersStep";
import { DetailsStep } from "./steps/DetailsStep";
import { PhotoStep, MAX_PHOTO_BYTES, type PhotoPick } from "./steps/PhotoStep";
import {
  EMPTY_DRAFT,
  partnersForType,
  validateStep2,
  validateStep3,
  type FieldErrors,
  type JoinDraft,
  type PartnerDraft,
} from "./schema";

export function JoinWizard() {
  const t = useTranslations("join");
  const locale = useLocale();
  const tCommon = useTranslations("common");
  const router = useRouter();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState<JoinDraft>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [picks, setPicks] = useState<PhotoPick[]>([]);
  // Namespace-relative i18n key for the missing-photo error on the final step.
  const [photoErrorKey, setPhotoErrorKey] = useState<string | null>(null);
  // Namespace-relative i18n key for a page-level error, or null.
  const [serverErrorKey, setServerErrorKey] = useState<string | null>(null);
  const turnstile = useTurnstile();
  const [submitting, setSubmitting] = useState(false);
  // A restored draft session shows a dismissible "picking up where you left
  // off" notice with a start-over escape hatch.
  const [showResumeNotice, setShowResumeNotice] = useState(false);
  // Draft persistence stays off until the mount effect has restored (or ruled
  // out) a saved session, so we never overwrite a draft with the empty form.
  const [hydrated, setHydrated] = useState(false);
  const stepRef = useRef<HTMLDivElement>(null);
  const picksRef = useRef<PhotoPick[]>(picks);

  function focusStep() {
    requestAnimationFrame(() => stepRef.current?.focus());
  }

  function focusFirstInvalid() {
    requestAnimationFrame(() => {
      const target =
        stepRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]') ??
        stepRef.current;
      target?.focus();
    });
  }

  // Keep the mount-only cleanup below pointed at the latest picks rather than
  // the empty array captured by the wizard's first render.
  useEffect(() => {
    picksRef.current = picks;
  }, [picks]);

  // Revoke preview object URLs on unmount to avoid leaking blobs.
  useEffect(() => {
    return () => picksRef.current.forEach((p) => URL.revokeObjectURL(p.url));
  }, []);

  // Mount: bounce signed-in members to where they belong, then restore any
  // saved draft session (fields + step from localStorage, photos from
  // IndexedDB). Read in an effect — never at render — to avoid hydration
  // mismatches.
  useEffect(() => {
    const me = getMe();
    if (me) {
      router.replace(me.status === "pending" ? "/pending" : "/deck");
      return;
    }
    let active = true;
    const saved = loadJoinDraft();
    /* eslint-disable react-hooks/set-state-in-effect -- one-time restore */
    if (saved) {
      setDraft((d) => ({ ...d, ...saved.fields }));
      // A saved step beyond 1 always has a profile type; clamp defensively.
      setStep(saved.fields.profileType ? Math.min(4, Math.max(1, saved.step)) : 1);
      setShowResumeNotice(true);
    }
    setHydrated(true);
    /* eslint-enable react-hooks/set-state-in-effect */
    listDraftPhotos().then((stored) => {
      if (!active || stored.length === 0) return;
      setPicks((prev) => [
        ...prev,
        ...stored.map(({ id, file }) => ({
          file,
          url: URL.createObjectURL(file),
          idempotencyKey: id,
          draftId: id,
        })),
      ]);
      setShowResumeNotice(true);
    });
    return () => {
      active = false;
    };
  }, [router]);

  // Persist fields + step (never the password) debounced, so a reload or crash
  // resumes where the user left off. Skipped while pristine (nothing entered),
  // during submit, and until the restore above has run.
  useEffect(() => {
    if (!hydrated || submitting) return;
    if (draft.profileType === null && step === 1) return;
    const handle = setTimeout(() => saveJoinDraft(draft, step), 400);
    return () => clearTimeout(handle);
  }, [draft, step, hydrated, submitting]);

  function addPhotos(list: FileList | null) {
    if (!list) return;
    const next: PhotoPick[] = [];
    let rejected = 0;
    for (const file of Array.from(list)) {
      if (!file.type.startsWith("image/") || file.size > MAX_PHOTO_BYTES) {
        rejected++;
        continue;
      }
      const url = URL.createObjectURL(file);
      const idempotencyKey = newUploadIdempotencyKey();
      next.push({ file, url, idempotencyKey });
      // Write-through to the draft store; attach the id once known (or drop
      // the orphaned blob if the pick was removed in the meantime).
      void addDraftPhoto(file, idempotencyKey).then((draftId) => {
        if (!draftId) return;
        setPicks((prev) => {
          if (!prev.some((p) => p.url === url)) {
            void removeDraftPhoto(draftId);
            return prev;
          }
          return prev.map((p) => (p.url === url ? { ...p, draftId } : p));
        });
      });
    }
    if (rejected > 0) {
      toast({
        title: t("photos.skippedTitle", { count: rejected }),
        description: t("photos.skippedBody"),
        variant: "danger",
      });
    }
    if (next.length > 0) {
      setPicks((prev) => [...prev, ...next]);
      setPhotoErrorKey(null);
    }
  }

  function removePhoto(index: number) {
    setPicks((prev) => {
      URL.revokeObjectURL(prev[index].url);
      const draftId = prev[index].draftId;
      if (draftId) void removeDraftPhoto(draftId);
      return prev.filter((_, i) => i !== index);
    });
  }

  /** Discards the restored session: both draft stores and all wizard state. */
  function startOver() {
    clearJoinDraft();
    void clearDraftPhotos();
    picks.forEach((p) => URL.revokeObjectURL(p.url));
    setPicks([]);
    setDraft(EMPTY_DRAFT);
    setStep(1);
    setErrors({});
    setPhotoErrorKey(null);
    setServerErrorKey(null);
    setShowResumeNotice(false);
  }

  function chooseType(type: ProfileType) {
    setDraft((d) => ({ ...d, profileType: type, partners: partnersForType(type) }));
    setErrors({});
  }

  function patchPartner(index: number, patch: Partial<PartnerDraft>) {
    setDraft((d) => ({
      ...d,
      partners: d.partners.map((p, i) => (i === index ? { ...p, ...patch } : p)),
    }));
  }

  function toggleSeeking(type: ProfileType) {
    setDraft((d) => ({
      ...d,
      seeking: d.seeking.includes(type)
        ? d.seeking.filter((t) => t !== type)
        : [...d.seeking, type],
    }));
    setErrors((e) => ({ ...e, seeking: "" }));
  }

  function patchField(patch: Partial<JoinDraft>) {
    setDraft((d) => ({ ...d, ...patch }));
    setErrors((e) => {
      const next = { ...e };
      for (const key of Object.keys(patch)) delete next[key];
      return next;
    });
  }

  function next() {
    setServerErrorKey(null);
    if (step === 2) {
      const e = validateStep2(draft);
      setErrors(e);
      if (Object.keys(e).length > 0) {
        focusFirstInvalid();
        return;
      }
    }
    if (step === 3) {
      const e = validateStep3(draft);
      setErrors(e);
      if (Object.keys(e).length > 0) {
        focusFirstInvalid();
        return;
      }
    }
    setStep((s) => Math.min(4, s + 1));
    focusStep();
  }

  function back() {
    setServerErrorKey(null);
    setStep((s) => Math.max(1, s - 1));
  }

  async function submit() {
    // Details were validated on step 3; re-check and bounce back if tampered.
    const e = validateStep3(draft);
    setErrors(e);
    if (Object.keys(e).length > 0) {
      setStep(3);
      return;
    }
    if (!draft.profileType) return;
    if (picks.length === 0) {
      setPhotoErrorKey("photos.requiredError");
      return;
    }

    setSubmitting(true);
    setServerErrorKey(null);
    try {
      const { user } = await register({
        email: draft.email.trim(),
        password: draft.password,
        username: draft.username,
        profileType: draft.profileType,
        seeking: draft.seeking,
        country: draft.country,
        locale,
        city: draft.virtualOnly ? undefined : draft.city.trim() || undefined,
        cityId: draft.virtualOnly ? undefined : (draft.cityId ?? undefined),
        virtualOnly: draft.virtualOnly || undefined,
        partners: draft.partners.map((p) => ({
          gender: p.gender,
          birthDate: p.birthDate,
          bio: p.bio.trim() || undefined,
        })),
        consent: draft.consent,
        turnstileToken: turnstile.token ?? undefined,
      });
      // Register issued a session; persist identity so the waiting room and the
      // app shell recognise the (still pending) member after redirect.
      saveMe({
        username: user.username,
        profileType: user.profileType,
        role: user.role,
        status: user.status,
      });
      // The account exists — the saved form draft has served its purpose.
      clearJoinDraft();
    } catch (err) {
      handleServerError(err);
      setSubmitting(false);
      return;
    }

    // The account now exists and register issued a session. Upload the
    // onboarding photos; a failure here must NOT lose the account — the user is
    // pending regardless, so we still land on /pending and nudge them to retry.
    // Blobs that fail to upload stay in the draft store so the waiting room
    // can offer them pre-picked.
    try {
      for (const p of picks) {
        await uploadMedia(p.file, { idempotencyKey: p.idempotencyKey });
        if (p.draftId) await removeDraftPhoto(p.draftId);
      }
      await clearDraftPhotos();
    } catch {
      toast({
        title: t("photos.uploadFailedTitle"),
        description: t("photos.uploadFailedBody"),
        variant: "danger",
      });
    }
    router.push("/pending");
  }

  function handleServerError(err: unknown) {
    if (!(err instanceof ApiError)) {
      setServerErrorKey("errors.registerFailed");
      return;
    }
    switch (err.code) {
      case "EMAIL_TAKEN":
        setErrors((e) => ({ ...e, email: "errors.emailTaken" }));
        setStep(3);
        break;
      case "USERNAME_TAKEN":
        setErrors((e) => ({ ...e, username: "errors.usernameTaken" }));
        setStep(3);
        break;
      case "UNDERAGE":
        setStep(2);
        setServerErrorKey("errors.underage");
        break;
      case "RATE_LIMITED":
        setServerErrorKey("errors.rateLimited");
        break;
      case "TURNSTILE_FAILED":
        turnstile.reset();
        setServerErrorKey("errors.turnstileFailed");
        break;
      default:
        setServerErrorKey("errors.registerFailed");
    }
  }

  const canContinue = step !== 1 || draft.profileType !== null;

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="border-b border-white/8 px-5 py-4">
        <div className="mx-auto flex w-full max-w-2xl items-center justify-between">
          <Wordmark size="sm" />
          <Link
            href="/login"
            className="text-sm text-white/55 transition-colors hover:text-white/85"
          >
            {t("signIn")}
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-2xl flex-1 px-5 py-8">
        <div className="mb-8">
          <Stepper current={step} />
        </div>

        {showResumeNotice && (
          <div
            role="status"
            className="mb-6 flex items-center gap-3 rounded-xl border border-flamingo/25 bg-flamingo/10 px-4 py-3 text-sm text-flamingo-light"
          >
            <p className="flex-1">{t("resume.notice")}</p>
            <button
              type="button"
              onClick={startOver}
              className="shrink-0 font-medium underline decoration-flamingo/40 underline-offset-2 transition-colors hover:text-white"
            >
              {t("resume.startOver")}
            </button>
            <button
              type="button"
              onClick={() => setShowResumeNotice(false)}
              aria-label={tCommon("close")}
              className="flex size-6 shrink-0 items-center justify-center rounded-full text-flamingo-light/70 transition-colors hover:bg-white/10 hover:text-white"
            >
              <svg viewBox="0 0 24 24" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
        )}

        <div
          ref={stepRef}
          tabIndex={-1}
          role="group"
          aria-label={t("stepper.progress", { n: step, total: 4 })}
          className="animate-rise outline-none"
          key={step}
        >
          {step === 1 && (
            <AccountTypeStep value={draft.profileType} onChange={chooseType} />
          )}
          {step === 2 && draft.profileType && (
            <PartnersStep
              profileType={draft.profileType}
              partners={draft.partners}
              errors={errors}
              onChange={patchPartner}
            />
          )}
          {step === 3 && (
            <DetailsStep
              draft={draft}
              errors={errors}
              onToggleSeeking={toggleSeeking}
              onField={patchField}
            />
          )}
          {step === 4 && (
            <PhotoStep
              picks={picks}
              errorKey={photoErrorKey}
              onAdd={addPhotos}
              onRemove={removePhoto}
            />
          )}
        </div>

        {step === 4 && turnstile.enabled && turnstile.siteKey && (
          <TurnstileWidget
            key={turnstile.resetKey}
            siteKey={turnstile.siteKey}
            onToken={turnstile.setToken}
          />
        )}

        {step === 3 && (
          <div className="mt-8">
            <label className="flex cursor-pointer items-start gap-2.5 text-sm text-white/75">
              <input
                type="checkbox"
                checked={draft.consent}
                onChange={(e) => patchField({ consent: e.target.checked })}
                className="mt-0.5 size-4 shrink-0 accent-flamingo"
              />
              <span>
                {t.rich("consentLabel", {
                  terms: (chunks) => (
                    <Link href="/terms" className="text-flamingo/80 hover:text-flamingo">
                      {chunks}
                    </Link>
                  ),
                  privacy: (chunks) => (
                    <Link href="/privacy" className="text-flamingo/80 hover:text-flamingo">
                      {chunks}
                    </Link>
                  ),
                })}
              </span>
            </label>
            {errors.consent && (
              <p
                role="alert"
                className="mt-2 text-xs font-medium text-danger-light"
              >
                {t(errors.consent)}
              </p>
            )}
          </div>
        )}

        {serverErrorKey && (
          <p
            role="alert"
            className="mt-6 rounded-xl border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger-light"
          >
            {t(serverErrorKey)}
          </p>
        )}

        <div className="mt-8 flex items-center gap-3">
          {step > 1 && (
            <Button variant="ghost" onClick={back} disabled={submitting}>
              {tCommon("back")}
            </Button>
          )}
          <div className="flex-1" />
          {step < 4 ? (
            <Button onClick={next} disabled={!canContinue}>
              {t("continue")}
            </Button>
          ) : (
            <Button onClick={submit} isLoading={submitting}>
              {t("createAccount")}
            </Button>
          )}
        </div>

        <p className="mt-6 text-center text-xs leading-relaxed text-white/50">
          {t.rich("legal", {
            terms: (chunks) => (
              <Link href="/terms" className="text-flamingo/80 hover:text-flamingo">
                {chunks}
              </Link>
            ),
            privacy: (chunks) => (
              <Link href="/privacy" className="text-flamingo/80 hover:text-flamingo">
                {chunks}
              </Link>
            ),
          })}
        </p>
      </main>
    </div>
  );
}
